import { FunctionComponent, useCallback, useMemo } from "react";
import { TabButton } from "@ui-kit/TabButton/TabButton";
import { ExternalIcon } from "@icons/ExternalIcon";
import { colors } from "@ui-kit/ConfigProvider/ConfigProvider";
import { useAppDispatch, useAppSelector } from "@store/store";
import {
  selectIsMediaActive,
  selectIsPlayingMedia,
  setIsPlayingAction,
  setMediaAction,
} from "@store/media/mediaReducer";
import type { Place } from "@dto/Place";
import type { Tour } from "@dto/Tour";
import type { Event } from "@dto/Event";
import { useTranslation } from "react-i18next";
import { Goal } from "@dto/Goal";
import { FeedPost } from "@dto/FeedPost";
import { Plannable } from "@dto/Plannable";

interface MediaPlayerProps {
  entity: Place | Tour | Event | Goal | FeedPost | Plannable;
  variant?: "list" | "flyout";
}

const AudioPlayerButton: FunctionComponent<MediaPlayerProps> = ({
  entity,
  variant = "flyout",
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const entityMedia = entity.audioMediaFiles?.[0];
  const isPlaying = useAppSelector(selectIsPlayingMedia(entityMedia?.url));
  const isMediaActive = useAppSelector(selectIsMediaActive(entityMedia?.url));

  const label = useMemo(() => {
    if (isPlaying) {
      return t("common.pause");
    }

    return isMediaActive ? t("common.resume") : t("common.listen");
  }, [isPlaying, isMediaActive, t]);

  const handlePress = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();

      if (isMediaActive) {
        dispatch(setIsPlayingAction(!isPlaying));
      } else {
        dispatch(
          setMediaAction({
            ...entityMedia,
            title: entity?.name ?? "",
          }),
        );
      }
    },
    [dispatch, entity?.name, entityMedia, isMediaActive, isPlaying],
  );

  if (!entityMedia || !entityMedia.url) {
    return null;
  }

  return (
    <div>
      <TabButton
        label={label}
        size={variant === "list" ? "small" : "default"}
        variant="default"
        iconPrefix={
          <ExternalIcon
            svgName={isPlaying ? "pause" : "play"}
            size={16}
            color={colors.neutral700}
          />
        }
        onPress={handlePress}
      />
    </div>
  );
};

export { AudioPlayerButton };
